import classNames from 'classnames'
import { useState, useCallback } from 'react'
import { useNavigate } from 'react-router-dom'

import type { ReactElement } from 'react'
import type { ISwitchButton } from '~/components/Buttons/SwitchButton'
import type { ITemplate } from '~/types/template'

import TemplateDefaultPreviewLandscape from '~/assets/template-default-preview-landscape.svg'
import TemplateDefaultPreviewPortrait from '~/assets/template-default-preview-portrait.svg'
import Button from '~/components/Buttons/Button'
import SwitchButton from '~/components/Buttons/SwitchButton'
import { ModalBody, ModalFooter, ModalHeader } from '~/components/Modal/Layout'
import { getTemplateFromKey } from '~/utils/template'

import './styles.scss'

interface Props {
  templateDetails?: ITemplate
}

const TemplateModal = ({ templateDetails }: Props): ReactElement => {
  const navigate = useNavigate()

  const [switchButtons, setSwitchButtons] = useState<ISwitchButton[]>([
    {
      content: 'Portrait',
      id: 'portrait',
      isActive: true,
    },
    {
      content: 'Landscape',
      id: 'landscape',
      isActive: false,
    },
  ])

  const handleCreatePageContainer = () => {
    navigate('/dynamic-screens/page-containers/creation', {
      state: {
        deviceType: templateDetails?.device_type,
        selectedTemplate: templateDetails?.template_key,
        selectedTemplateVersion: templateDetails?.version,
      },
    })
  }

  const getPreviewOrientation = () => {
    if (templateDetails?.orientation === 'both') {
      return switchButtons.find(({ isActive }) => isActive)?.id
    }
    return templateDetails?.orientation
  }

  const previewContainerClassNames = classNames('preview-container', {
    [getPreviewOrientation() || '']: true,
  })

  const isCreateButtonDisabled = (templateKey: string) => {
    return !getTemplateFromKey(templateKey)
  }

  const handleSelectionChange = useCallback(
    (buttonId: string) => {
      setSwitchButtons(
        switchButtons.map((button) => ({
          ...button,
          isActive: button.id === buttonId,
        }))
      )
    },
    [switchButtons]
  )

  return (
    <>
      <ModalHeader>{templateDetails?.title}</ModalHeader>
      <ModalBody>
        <div className="modal-body-container">
          {templateDetails?.orientation === 'both' ? (
            <div className="orientation-switch-container">
              <SwitchButton buttons={switchButtons} onButtonSelectionChange={handleSelectionChange} size="small" />
            </div>
          ) : null}
          <div className={previewContainerClassNames}>
            <img
              src={
                getPreviewOrientation() === 'landscape'
                  ? TemplateDefaultPreviewLandscape
                  : TemplateDefaultPreviewPortrait
              }
            />
          </div>
        </div>
      </ModalBody>
      <ModalFooter>
        {templateDetails && (
          <Button
            className="create-page-container-button"
            disabled={isCreateButtonDisabled(templateDetails?.template_key)}
            onClick={handleCreatePageContainer}
          >
            Create page container
          </Button>
        )}
      </ModalFooter>
    </>
  )
}

export default TemplateModal
